import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_RETURNS_API ?? '/api/v1';

export type ReturnRequest = {
  id: string;
  return_number: string;
  order_id: string;
  status: string;
  reason_code: string;
  refund_amount: number;
  currency: string;
  health_score: number;
  fraud_risk: number;
  created_at: string;
};

export type RMA = {
  id: string;
  return_id: string;
  rma_number: string;
  status: string;
  barcode?: string;
  label_url?: string;
};

export type Refund = {
  id: string;
  return_id: string;
  refund_number: string;
  status: string;
  amount: number;
  currency: string;
  method: string;
};

export type Claim = {
  id: string;
  claim_number: string;
  type: string;
  status: string;
  amount: number;
};

export type ReturnsDashboard = {
  total_returns: number;
  pending_approval: number;
  in_transit: number;
  completed: number;
  total_refunds: number;
  refund_amount: number;
  open_claims: number;
  rto_count: number;
  avg_health_score: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const returnsApi = {
  dashboard: () => request<ReturnsDashboard>('/returns/dashboard'),

  listReturns: () => request<ReturnRequest[]>('/returns'),

  createReturn: (body: { order_id: string; reason_code: string; items: { product_id: string; quantity: number; unit_price: number }[] }) =>
    request<ReturnRequest>('/returns', { method: 'POST', body: JSON.stringify(body) }),

  approveReturn: (id: string) =>
    request<ReturnRequest>(`/returns/${id}/approve`, { method: 'POST', body: '{}' }),

  createRMA: (returnId: string) =>
    request<RMA>('/rma', { method: 'POST', body: JSON.stringify({ return_id: returnId }) }),

  listRefunds: () => request<Refund[]>('/refunds'),

  createRefund: (body: { return_id: string; amount: number; currency?: string }) =>
    request<Refund>('/refunds', { method: 'POST', body: JSON.stringify(body) }),

  approveRefund: (id: string) =>
    request<Refund>(`/refunds/${id}/approve`, { method: 'POST', body: '{}' }),

  scheduleReversePickup: (returnId: string) =>
    request<unknown>('/reverse/pickup', { method: 'POST', body: JSON.stringify({ return_id: returnId }) }),

  listClaims: () => request<Claim[]>('/claims'),

  createClaim: (body: { type: string; amount: number; return_id?: string }) =>
    request<Claim>('/claims', { method: 'POST', body: JSON.stringify(body) }),

  listRTO: () => request<unknown[]>('/rto'),

  processRTO: (body: { shipment_id: string; order_id: string }) =>
    request<unknown>('/rto/process', { method: 'POST', body: JSON.stringify(body) }),
};
